/*
 * Manual Handling Safety — Stop & Check system, unsafe-request scripts and
 * protective documentation guidance. Rendered inside the Shift Kit AND on
 * the Safety & Escalation category page — single source, no duplication.
 */
import { ShieldCheck, Info } from "lucide-react";
import { Para, H4, Bullets, Callout, SECTION_HEADING } from "./HubText";

export default function ManualHandlingSafety() {
  return (
    <div id="manual-handling-safety" className="rounded-3xl border border-slate-200 bg-white p-7 md:p-10 scroll-mt-24" data-testid="manual-handling-safety">
      <div className="overline text-teal mb-2 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" strokeWidth={2} /> Manual Handling Safety
      </div>
      <h3 className={SECTION_HEADING}>This section is here to protect you.</h3>
      <Para>Manual handling is one of the biggest risk areas in care. These tools help you recognise unsafe situations, respond professionally, document what happened and protect yourself if something goes wrong.</Para>

      <H4>Stop &amp; Check System</H4>
      <div className="grid md:grid-cols-3 gap-4 mt-4">
        <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-5">
          <div className="font-display font-semibold text-red-900 mb-2">🟥 Do not proceed if any of these apply</div>
          <ul className="text-sm text-red-900 space-y-1.5 leading-relaxed">
            <li>• Client cannot weight bear at all</li>
            <li>• Client is heavy and you are alone</li>
            <li>• Client is confused/unpredictable</li>
            <li>• No equipment available (when clearly needed)</li>
            <li>• You have not been shown how to perform the move</li>
            <li>• You feel physical strain just thinking about it</li>
          </ul>
        </div>
        <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-5">
          <div className="font-display font-semibold text-amber-900 mb-2">🟡 Proceed with caution</div>
          <ul className="text-sm text-amber-900 space-y-1.5 leading-relaxed">
            <li>• Client mobility fluctuates</li>
            <li>• Environment is tight/cluttered</li>
            <li>• Equipment is available but unfamiliar</li>
            <li>• Family are &ldquo;advising&rdquo; instead of a care plan</li>
          </ul>
          <div className="mt-3 text-xs text-amber-900"><strong>How:</strong> slow down, reassess, ask for guidance.</div>
        </div>
        <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5">
          <div className="font-display font-semibold text-emerald-900 mb-2">🟢 Generally safe</div>
          <ul className="text-sm text-emerald-900 space-y-1.5 leading-relaxed">
            <li>• Clear care/handling plan in place</li>
            <li>• Correct equipment provided</li>
            <li>• You’ve been trained</li>
            <li>• Client can assist</li>
            <li>• Environment is safe</li>
          </ul>
        </div>
      </div>
      <Callout tone="purple" title="Carer protection rule">
        “If it hasn’t been assessed, it hasn’t been approved.”
      </Callout>

      <H4>Unsafe-request response scripts</H4>
      <div className="grid md:grid-cols-2 gap-4 mt-3">
        {[
          { title: "When asked to do something unsafe", text: "I’m not comfortable carrying out that transfer as it hasn’t been assessed as safe. I want to make sure both you and I are protected." },
          { title: "When family say “we always do it like this”", text: "I understand that’s how it’s been done, but I have to follow safe practice. If anything went wrong, it would put us both at risk." },
          { title: "When pressured", text: "I’m happy to help, but only in a way that’s safe and within my training. We may need to look at equipment or reassessment." },
          { title: "When you need to stop mid-task", text: "I’m going to stop this transfer as it’s not safe to continue. Let’s reassess before trying again." },
        ].map((s, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-[#f7f7f3] p-5">
            <div className="text-xs font-semibold text-ink-soft uppercase tracking-wider mb-1">{s.title}</div>
            <p className="text-sm text-ink leading-relaxed italic">“{s.text}”</p>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-ink-soft">Tone: calm, professional, not emotional. You are not refusing care — you are protecting it.</p>

      <H4>Documentation that protects you</H4>
      <Bullets color="text-teal" items={[
        "Daily notes — record mobility status changes, refusal of care, any difficulty with transfers, any discomfort/pain",
        "Refusal of care record — what was offered, what was refused, client understanding, risks explained, outcome",
        "Escalation record — what concern was raised, who you told, when, response",
        "Equipment concern note — “Transfer attempted with current equipment. Concerns regarding safety identified. Recommend reassessment.”",
      ]} />
      <Callout tone="slate" title="The simple truth" icon={Info}>
        Carers don’t get into trouble for saying no. They get into trouble for doing something unsafe and hoping it’s fine.
      </Callout>
    </div>
  );
}
